import type { CapacityContributionLocationType as LocationType, WorkingHoursOverviewRecord } from '@navio/nsdk';
import { CapacityContributionLocationType, CapacityContributionType } from '@navio/nsdk';
import { byStart, type ShiftInterval } from './workingHoursShiftLabels';

/**
 * What the working-hours board can show: bookable time per channel, and worker shifts with
 * hours nobody can book (ad hoc tasks, a tire hotel day). Also the `wh-show` query param's
 * values. The garage value keeps the backend's word; the label is "Service center".
 */
export type WorkingHoursShowOption = 'mobile' | 'garage' | 'not-bookable';

export const SHOW_OPTION_BY_LOCATION_TYPE: Record<LocationType, WorkingHoursShowOption> = {
  [CapacityContributionLocationType.MOBILE]: 'mobile',
  [CapacityContributionLocationType.STATIONARY]: 'garage'
};

/** Mobile first, then garage — the board's reading order. */
const CHANNEL_ORDER: LocationType[] = [
  CapacityContributionLocationType.MOBILE,
  CapacityContributionLocationType.STATIONARY
];

/** The options a department can pick: the channels it runs, then Not fully bookable, which every department has. */
export const workingHoursShowOptions = (locationTypes: LocationType[]): WorkingHoursShowOption[] => [
  ...CHANNEL_ORDER.filter((locationType) => locationTypes.includes(locationType)).map(
    (locationType) => SHOW_OPTION_BY_LOCATION_TYPE[locationType]
  ),
  'not-bookable'
];

/**
 * The options the param picks among those offered, in their order. Empty means nothing is
 * narrowed and every option shows; picking every option reads the same, so the mixed view
 * has one spelling — no param.
 */
export const pickedWorkingHoursShow = (
  param: readonly (string | null)[] | null | undefined,
  offered: WorkingHoursShowOption[]
): WorkingHoursShowOption[] => {
  const picked = offered.filter((option) => param?.includes(option));
  return picked.length === offered.length ? [] : picked;
};

/** The channels whose bookable time is on show, mobile first. */
export const channelsOnShow = (show: WorkingHoursShowOption[]): LocationType[] =>
  CHANNEL_ORDER.filter((locationType) => show.includes(SHOW_OPTION_BY_LOCATION_TYPE[locationType]));

/** One worker shift as the Show filter reads it. */
export type WorkingHoursShiftShow = {
  workerShiftId: number;
  date: string;
  /** Each channel it holds bookable time on, then Not fully bookable when any of its hours are not. */
  options: WorkingHoursShowOption[];
  /**
   * Its hours outside every bookable window, on any channel. Empty when bookable time covers
   * the whole shift. Planned breaks play no part: bookable time runs through them.
   */
  notBookableIntervals: ShiftInterval[];
};

/** The parts of `shift` that none of `covering` overlaps, in order, on the records' own timestamps. */
export const uncoveredIntervals = (shift: ShiftInterval, covering: ShiftInterval[]): ShiftInterval[] => {
  const shiftEnd = Date.parse(shift.endsAt);
  const { parts, from } = [...covering].sort(byStart).reduce(
    (state, window) => {
      const cursor = Date.parse(state.from);
      const windowStart = Date.parse(window.startsAt);
      const gap: ShiftInterval[] =
        Math.min(windowStart, shiftEnd) > cursor
          ? [{ startsAt: state.from, endsAt: windowStart < shiftEnd ? window.startsAt : shift.endsAt }]
          : [];
      return {
        parts: [...state.parts, ...gap],
        from: Date.parse(window.endsAt) > cursor ? window.endsAt : state.from
      };
    },
    { parts: [] as ShiftInterval[], from: shift.startsAt }
  );
  return Date.parse(from) < shiftEnd ? [...parts, { startsAt: from, endsAt: shift.endsAt }] : parts;
};

const toShiftShow = ({
  workerShiftId,
  date,
  locationTypes,
  notBookableIntervals
}: {
  workerShiftId: number;
  date: string;
  locationTypes: LocationType[];
  notBookableIntervals: ShiftInterval[];
}): WorkingHoursShiftShow => ({
  workerShiftId,
  date,
  options: [
    ...CHANNEL_ORDER.filter((locationType) => locationTypes.includes(locationType)).map(
      (locationType) => SHOW_OPTION_BY_LOCATION_TYPE[locationType]
    ),
    ...(notBookableIntervals.length > 0 ? ['not-bookable' as const] : [])
  ],
  notBookableIntervals
});

type BookableWorkerShift = {
  serviceWorkerId: number;
  date: string;
  shiftInterval: ShiftInterval | null;
  locationTypes: Set<LocationType>;
  intervals: ShiftInterval[];
};

/**
 * Every named worker's shifts in the range as the Show filter reads them, by worker and then by
 * worker shift. A shift is not bookable in part when any of its hours sit outside the bookable
 * time on every channel together: on shift 08:00–16:00 and bookable 10:00–11:00 matches Mobile
 * and Not fully bookable alike. A shift with no bookable time matches Not fully bookable only.
 */
export const workerShiftsForShow = (
  record: Pick<WorkingHoursOverviewRecord, 'shifts' | 'shiftsWithoutBookableCapacity'>
): Map<number, Map<number, WorkingHoursShiftShow>> => {
  const bookableShifts = new Map<number, BookableWorkerShift>();
  for (const shift of record.shifts) {
    if (
      shift.contributionType === CapacityContributionType.BASELINE ||
      shift.serviceWorkerId == null ||
      shift.workerShiftId == null
    ) {
      continue;
    }
    const entry = bookableShifts.get(shift.workerShiftId) ?? {
      serviceWorkerId: shift.serviceWorkerId,
      date: shift.date,
      shiftInterval:
        shift.workerShiftStartsAt && shift.workerShiftEndsAt
          ? { startsAt: shift.workerShiftStartsAt, endsAt: shift.workerShiftEndsAt }
          : null,
      locationTypes: new Set<LocationType>(),
      intervals: []
    };
    entry.locationTypes.add(shift.locationType);
    entry.intervals.push(...shift.intervals);
    bookableShifts.set(shift.workerShiftId, entry);
  }

  const byWorker = new Map<number, Map<number, WorkingHoursShiftShow>>();
  const add = (serviceWorkerId: number, shiftShow: WorkingHoursShiftShow) =>
    byWorker.set(
      serviceWorkerId,
      (byWorker.get(serviceWorkerId) ?? new Map<number, WorkingHoursShiftShow>()).set(
        shiftShow.workerShiftId,
        shiftShow
      )
    );

  for (const [workerShiftId, entry] of bookableShifts) {
    add(
      entry.serviceWorkerId,
      toShiftShow({
        workerShiftId,
        date: entry.date,
        locationTypes: [...entry.locationTypes],
        // Without the shift's own hours there is nothing to measure against: the bookable time is all there is.
        notBookableIntervals: entry.shiftInterval ? uncoveredIntervals(entry.shiftInterval, entry.intervals) : []
      })
    );
  }
  // Bookable time with bookings stopped counts as none: nothing on the shift can be booked now, so it
  // matches Not fully bookable, never the channel it was stopped on.
  for (const workerShift of record.shiftsWithoutBookableCapacity) {
    add(
      workerShift.serviceWorkerId,
      toShiftShow({
        workerShiftId: workerShift.workerShiftId,
        date: workerShift.date,
        locationTypes: [],
        notBookableIntervals: [{ startsAt: workerShift.startsAt, endsAt: workerShift.endsAt }]
      })
    );
  }
  return byWorker;
};

/**
 * Whether a worker shift shows on a channel's line. Shown for that channel, or for Not fully
 * bookable when part of the shift is not — so with only that picked, a shift bookable 10:00–11:00 of
 * 08:00–16:00 still shows, on its channel's line.
 */
export const showsOnChannelLine = (
  shiftOptions: WorkingHoursShowOption[],
  locationType: LocationType,
  show: WorkingHoursShowOption[]
) =>
  show.includes(SHOW_OPTION_BY_LOCATION_TYPE[locationType]) ||
  (show.includes('not-bookable') && shiftOptions.includes('not-bookable'));
